<template>
    <div
        v-gtm:@click
        v-gtm:label="oddsGtmLabel"
        class="oddsArea outrightOdds notranslate"
        v-on:click="handleClick"
    >
        <div
            v-bind:class="['odds', {
                live: oddsInfo.isLive,
                'odds-rise': isOddsRise,
                'odds-drop': isOddsDrop,
                'odds-picked': isSelected,
                'odds-negative': isNegative,
            }]"
        >
            <div
                class="oddsValue"
                v-bind:style="{ textAlign: align }"
            >
                {{ priceDisplay }}
            </div>
            <div class="oddsStates" v-bind:class="oddsChangeStates">
                <div v-if="oddsChangeStates" class="triangle" />
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { storeToRefs } from 'pinia';
    import { computed, toRefs } from 'vue';
    import { getTournamentCenterOddsGtmLabel } from '@/components/tournamentCenter/tournamentCenterHelper';
    import { useBetPage } from '@/composable/useBetPage';
    import { usePriceChangeStatus } from '@/composable/usePriceChangeStatus';
    import { formatMinDecimalPlace } from '@/core/lib/numberFormat';
    import type { IOutrightLeague } from '@/interface/IOutrightLeague';
    import type { IOutrightOdds } from '@/interface/IOutrightOdds';
    import { DisplaySection, PriceChangeStatus, TabName } from '@/interface/enum';

    import { useBetSlipStore } from '@/store/betSlipStore';
    import { useLayoutStore } from '@/store/layoutStore';

    const props = withDefaults(
        defineProps<{
            league: IOutrightLeague;
            oddsInfo: IOutrightOdds;
            align?: 'left' | 'center' | 'right';
            displaySection?: DisplaySection;
        }>(),
        {
            align: 'center',
            displaySection: DisplaySection.Unknown,
        },
    );

    const { league, oddsInfo, displaySection } = toRefs(props);
    const { singleTicket } = storeToRefs(useBetSlipStore());
    const oddsPrice = computed(() => oddsInfo.value.price);
    const isNegative = computed(() => oddsPrice.value < 0);
    const isSelected = computed(() => !!singleTicket.value
        && singleTicket.value.odds.id === oddsInfo.value.id);

    const { priceChangeStatus } = usePriceChangeStatus(oddsPrice);
    const isOddsRise = computed(() => priceChangeStatus.value === PriceChangeStatus.Rise);
    const isOddsDrop = computed(() => priceChangeStatus.value === PriceChangeStatus.Drop);
    const oddsChangeStates = computed(() => {
        if (isOddsRise.value) return 'oddsStates-rise';
        if (isOddsDrop.value) return 'oddsStates-drop';
        return '';
    });

    const { clickGtmLabel, unclickGtmLabel } = getTournamentCenterOddsGtmLabel(displaySection.value);
    const oddsGtmLabel = computed(() => (isSelected.value ? unclickGtmLabel : clickGtmLabel));

    const { isLayoutExpanded } = storeToRefs(useLayoutStore());
    const { toggleIsSportListExpanded, toggleBetSlipExpanded, toggleMyBetsExpanded, setBetSlipActiveTab } = useLayoutStore();
    const { addToOutrightBetSlip, removeSingleBetSlip } = useBetSlipStore();

    function handleClick() {
        const { betPage } = useBetPage(displaySection.value);

        if (isSelected.value) {
            removeSingleBetSlip();
        } else {
            addToOutrightBetSlip(league.value as IOutrightLeague, oddsInfo.value as IOutrightOdds, betPage.value);
        }

        setBetSlipActiveTab(TabName.Single);
        toggleIsSportListExpanded(false);
        toggleBetSlipExpanded(true);
        if (!isLayoutExpanded.value) toggleMyBetsExpanded(false);
    }
    const priceDisplay = computed(() => formatMinDecimalPlace(props.oddsInfo.price, 2));
</script>

<style lang="scss" scoped>
    @import "@/components/odds/Odds";
</style>
